import { classifyCommand, parseTestCounts, unwrapShell } from '@viko/protocol';
import { parseCommand, searchCounts, summarizePaths } from './lib/summarize.js';
export const ACTIVITY_STEPS_KEPT = 24;
export const ACTIVITY_QUIET_MS = 90_000;
const TARGETS_KEPT = 12;
export function emptyActivity() {
    return {
        startedAt: 0,
        lastEventAt: 0,
        busy: false,
        turns: 0,
        steps: [],
        open: {},
        files: new Set(),
        additions: 0,
        deletions: 0,
    };
}
/**
 * Consecutive calls of these kinds fold into one step. Routine commands (git fetch/rebase/status,
 * ls, …) stay folded; only milestones — test/check runs with exit codes and the agent's own
 * commits — get a row of their own, so every step appears exactly once.
 */
const GROUPED = new Set(['read', 'search', 'web', 'edit', 'command']);
function stepKind(e) {
    switch (e.toolKind) {
        case 'read':
            return 'read';
        case 'search':
            return 'search';
        case 'web':
            return 'web';
        case 'edit':
        case 'write':
            return 'edit';
        case 'subagent':
            return 'subagent';
        case 'mcp':
            return 'mcp';
        case 'image_gen':
            return 'image';
        case 'shell': {
            // Labeling only (never evidence): classify the command without cd/env/output plumbing.
            const c = e.command ? classifyCommand(parseCommand(e.command).title) : null;
            if (c)
                return c.kind === 'test' ? 'test' : 'check';
            return e.command && /(^|&&\s*|;\s*)git (-C \S+ )?commit\b/.test(unwrapShell(e.command))
                ? 'commit'
                : 'command';
        }
        // Bookkeeping tools are represented by their own events (todo list, plan, question).
        case 'todo':
        case 'plan':
        case 'question':
            return null;
        default:
            return 'other';
    }
}
function target(e) {
    if (e.command)
        return unwrapShell(e.command);
    if (e.toolKind === 'search')
        return searchQuery(e);
    if (e.path)
        return e.path;
    if (e.mcp)
        return `${e.mcp.server}.${e.mcp.tool}`;
    if (e.subagent?.description)
        return e.subagent.description;
    return e.title ?? e.name;
}
function searchQuery(e) {
    const input = (e.input ?? {});
    const q = typeof input.pattern === 'string'
        ? input.pattern
        : typeof input.query === 'string'
            ? input.query
            : undefined;
    return q ?? e.title ?? e.path ?? e.name;
}
const pushUnique = (list, x) => list.includes(x) ? list : [...list, x].slice(-TARGETS_KEPT);
function replaceStep(a, id, fn) {
    // The step being updated is almost always the newest one; search from the end.
    for (let i = a.steps.length - 1; i >= 0; i--) {
        const s = a.steps[i];
        if (s.id !== id)
            continue;
        const steps = a.steps.slice();
        steps[i] = fn(s);
        return steps;
    }
    return a.steps;
}
const diffCounts = (diffs) => {
    let add = 0;
    let del = 0;
    for (const d of diffs ?? []) {
        add += d.additions ?? 0;
        del += d.deletions ?? 0;
    }
    return { add, del };
};
/** Pure incremental activity reducer (except the append-only `files` set). */
export function reduceActivity(a, e) {
    // Daemon snapshots and quota readings say nothing about whether the agent is working.
    if (e.kind === 'progress' || e.kind === 'ratelimit')
        return a;
    const n = {
        ...a,
        startedAt: a.startedAt || e.ts,
        lastEventAt: Math.max(a.lastEventAt, e.ts),
    };
    switch (e.kind) {
        case 'run.start':
            n.startedAt = a.startedAt ? Math.min(a.startedAt, e.ts) : e.ts;
            break;
        case 'turn.start':
            n.turns = a.turns + 1;
            n.busy = true;
            n.lastOutcome = undefined;
            break;
        case 'turn.end': {
            n.busy = false;
            n.writing = undefined;
            n.lastOutcome = e.outcome;
            // Foreground calls cannot outlive their turn; background shells and agents can.
            const open = {};
            const closing = new Map();
            for (const [id, o] of Object.entries(a.open)) {
                const step = a.steps.find((s) => s.id === o.step);
                if (step?.background)
                    open[id] = o;
                else
                    closing.set(o.step, (closing.get(o.step) ?? 0) + 1);
            }
            n.open = open;
            if (closing.size)
                n.steps = a.steps.map((s) => closing.has(s.id)
                    ? {
                        ...s,
                        running: Math.max(0, s.running - (closing.get(s.id) ?? 0)),
                        ...(e.outcome !== 'ok'
                            ? { cancelled: s.cancelled + (closing.get(s.id) ?? 0) }
                            : {}),
                        endedAt: e.ts,
                    }
                    : s);
            break;
        }
        case 'status':
            n.busy = e.state === 'running' || e.state === 'compacting';
            break;
        case 'asst.delta':
            if (e.parentId)
                break;
            n.writing =
                a.writing?.blockId === e.blockId
                    ? a.writing
                    : { type: e.blockType, since: e.ts, blockId: e.blockId };
            break;
        case 'asst.block':
            if (e.parentId)
                break;
            if (a.writing?.blockId === e.blockId)
                n.writing = undefined;
            break;
        case 'tool.call': {
            if (e.parentId) {
                const parent = a.open[e.parentId];
                if (parent) {
                    const k = stepKind(e);
                    const detail = k ? `${VERB[k].running} ${target(e)}` : undefined;
                    if (detail)
                        n.steps = replaceStep(a, parent.step, (s) => ({ ...s, detail }));
                }
                break;
            }
            n.writing = undefined;
            const kind = stepKind(e);
            if (!kind)
                break;
            const t = target(e);
            const diff = diffCounts(e.diffs);
            const last = a.steps.at(-1);
            const call = {
                step: e.toolCallId,
                add: diff.add,
                del: diff.del,
                ...(e.command ? { command: e.command } : {}),
            };
            if (last && GROUPED.has(kind) && last.kind === kind && last.turnId === e.turnId) {
                call.step = last.id;
                n.steps = replaceStep(a, last.id, (s) => ({
                    ...s,
                    itemIds: [...s.itemIds, e.toolCallId].slice(-TARGETS_KEPT),
                    targets: pushUnique(s.targets, t),
                    calls: s.calls + 1,
                    running: s.running + 1,
                    lastStartedAt: e.ts,
                    endedAt: undefined,
                    // A folded group has no single exit status; failures are counted instead.
                    exitCode: undefined,
                    durationMs: undefined,
                }));
            }
            else {
                n.steps = [
                    ...a.steps,
                    {
                        id: e.toolCallId,
                        kind,
                        ...(e.turnId ? { turnId: e.turnId } : {}),
                        itemIds: [e.toolCallId],
                        targets: [t],
                        calls: 1,
                        running: 1,
                        errors: 0,
                        cancelled: 0,
                        startedAt: e.ts,
                        lastStartedAt: e.ts,
                        additions: 0,
                        deletions: 0,
                        ...(e.background ? { background: true } : {}),
                        ...(e.cwd ? { cwd: e.cwd } : {}),
                    },
                ].slice(-ACTIVITY_STEPS_KEPT);
            }
            n.open = { ...a.open, [e.toolCallId]: call };
            break;
        }
        case 'tool.result': {
            const call = a.open[e.toolCallId];
            if (!call)
                break;
            const open = { ...a.open };
            delete open[e.toolCallId];
            n.open = open;
            const ok = e.status === 'ok';
            const diff = e.diffs?.length ? diffCounts(e.diffs) : { add: call.add, del: call.del };
            if (ok && e.diffs?.length)
                for (const d of e.diffs)
                    n.files.add(d.path);
            if (ok) {
                n.additions = a.additions + diff.add;
                n.deletions = a.deletions + diff.del;
            }
            n.steps = replaceStep(a, call.step, (s) => {
                const next = {
                    ...s,
                    running: Math.max(0, s.running - 1),
                    errors: s.errors + (e.status === 'error' || (e.exitCode ?? 0) !== 0 ? 1 : 0),
                    cancelled: s.cancelled + (e.status === 'denied' || e.status === 'interrupted' ? 1 : 0),
                    endedAt: e.ts,
                    additions: s.additions + (ok ? diff.add : 0),
                    deletions: s.deletions + (ok ? diff.del : 0),
                };
                if (isShell(s.kind) && s.calls === 1) {
                    if (e.exitCode != null)
                        next.exitCode = e.exitCode;
                    next.durationMs = e.durationMs ?? e.ts - s.startedAt;
                    if (s.kind === 'test') {
                        const c = parseTestCounts(e.output);
                        if (c.passed != null)
                            next.passed = c.passed;
                        if (c.failed != null)
                            next.failed = c.failed;
                    }
                }
                if (s.kind === 'search' && ok) {
                    const c = searchCounts(e.output);
                    if (c.matches != null)
                        next.matches = (s.matches ?? 0) + c.matches;
                    if (c.files != null)
                        next.matchFiles = (s.matchFiles ?? 0) + c.files;
                }
                // Background agents answer "launched" at once; cli.task reports the real end.
                if (s.kind === 'subagent' && s.background && ok) {
                    next.running = s.running;
                    next.endedAt = undefined;
                    n.open = { ...n.open, [e.toolCallId]: call };
                }
                return next;
            });
            break;
        }
        case 'cli.task': {
            if (e.state !== 'completed' && e.state !== 'failed' && e.state !== 'stopped')
                break;
            const id = e.toolCallId;
            const call = id ? a.open[id] : undefined;
            if (!id || !call)
                break;
            const open = { ...a.open };
            delete open[id];
            n.open = open;
            n.steps = replaceStep(a, call.step, (s) => ({
                ...s,
                running: Math.max(0, s.running - 1),
                errors: s.errors + (e.state === 'failed' ? 1 : 0),
                cancelled: s.cancelled + (e.state === 'stopped' ? 1 : 0),
                endedAt: e.ts,
            }));
            break;
        }
    }
    return n;
}
export function stepStatus(s) {
    if (s.running > 0)
        return 'running';
    if (s.errors > 0 || (s.exitCode != null && s.exitCode !== 0))
        return 'error';
    if (s.cancelled > 0 && s.cancelled === s.calls)
        return 'cancelled';
    if (s.kind === 'test' && (s.failed ?? 0) > 0)
        return 'error';
    return 'ok';
}
const VERB = {
    read: { running: 'Reading', done: 'Read' },
    search: { running: 'Searching', done: 'Searched' },
    web: { running: 'Searching the web for', done: 'Searched the web for' },
    edit: { running: 'Editing', done: 'Edited' },
    command: { running: 'Running', done: 'Ran' },
    test: { running: 'Running tests:', done: 'Ran tests:' },
    check: { running: 'Running', done: 'Ran' },
    commit: { running: 'Committing:', done: 'Committed:' },
    subagent: { running: 'Subagent:', done: 'Subagent:' },
    mcp: { running: 'Using', done: 'Used' },
    image: { running: 'Generating', done: 'Generated' },
    other: { running: 'Using', done: 'Used' },
};
const NOUN = {
    read: ['file', 'files'],
    edit: ['file', 'files'],
    search: ['search', 'searches'],
    web: ['query', 'queries'],
    command: ['command', 'commands'],
};
const isShell = (k) => k === 'command' || k === 'test' || k === 'check' || k === 'commit';
/** Paths relative to the session's working directory (the repo), for scannable labels. */
export function relPath(p, cwd) {
    if (cwd && p.startsWith(`${cwd.replace(/\/$/, '')}/`))
        return p.slice(cwd.replace(/\/$/, '').length + 1);
    return p;
}
/** Plain-words label: "Reading 4 files", "Editing apps/daemon/src/setup/cli.ts", "Ran pnpm test". */
export function stepLabel(s, cwd) {
    const running = s.running > 0;
    const verb = VERB[s.kind][running ? 'running' : 'done'];
    const noun = NOUN[s.kind];
    const shown = (t) => s.kind === 'read' || s.kind === 'edit'
        ? relPath(t, cwd)
        : isShell(s.kind)
            ? parseCommand(t, cwd).title
            : t;
    const n = s.targets.length;
    // The current action names its newest target; a finished group is summarized.
    if (running && (s.kind === 'edit' || s.kind === 'command') && n > 1)
        return `${verb} ${shown(s.targets.at(-1) ?? '')}`;
    if ((s.kind === 'read' || s.kind === 'edit') && n > 1) {
        const p = summarizePaths(s.targets, cwd);
        return `${verb} ${p.count} files${p.where ? ` ${p.where}` : ''}`;
    }
    if (noun && n > 1)
        return `${verb} ${n} ${noun[1]}`;
    if (s.kind === 'commit')
        return `${verb} ${commitMessage(s.targets[0] ?? '')}`;
    if (s.kind === 'search')
        return `${verb} the codebase for “${s.targets[0] ?? ''}”`;
    if (s.kind === 'web')
        return `${verb} “${s.targets[0] ?? ''}”`;
    if (s.kind === 'image')
        return `${verb} an image`;
    return `${verb} ${shown(s.targets[0] ?? '')}`.trim();
}
/** Secondary line: grouped file names, search/test counts, exit status. */
export function stepDetail(s, cwd) {
    const parts = [];
    if ((s.kind === 'read' || s.kind === 'edit') && s.targets.length > 1)
        parts.push(summarizePaths(s.targets, cwd).names);
    if (s.kind === 'command' && s.targets.length > 1)
        parts.push(s.targets
            .map((t) => parseCommand(t, cwd).title.split(/\s+/).slice(0, 2).join(' '))
            .slice(-4)
            .join(', ') + (s.targets.length > 4 ? ', …' : ''));
    if (s.kind === 'search' && s.targets.length > 1)
        parts.push(s.targets
            .slice(-3)
            .map((t) => `“${t}”`)
            .join(', '));
    if (s.kind === 'search' && s.matches != null)
        parts.push(s.matchFiles != null
            ? `${s.matches} matches in ${s.matchFiles} files`
            : `${s.matches} matches`);
    else if (s.kind === 'search' && s.matchFiles != null)
        parts.push(`${s.matchFiles} files`);
    if (s.kind === 'test' && (s.passed != null || s.failed != null))
        parts.push(`${s.passed ?? 0} passed, ${s.failed ?? 0} failed`);
    if (s.exitCode != null && s.running === 0)
        parts.push(`exit ${s.exitCode}`);
    if (s.kind === 'subagent' && s.detail)
        parts.push(s.detail);
    if (s.errors && !(isShell(s.kind) && s.exitCode != null))
        parts.push(`${s.errors} failed`);
    return parts.length ? parts.join(' · ') : undefined;
}
function commitMessage(command) {
    const m = /-m\s+(?:"([^"]*)"|'([^']*)'|(\S+))/.exec(command);
    return (m?.[1] ?? m?.[2] ?? m?.[3] ?? command).split('\n')[0] ?? command;
}
/**
 * What to show right now. `asks` are unanswered question/approval ids with a short prompt; they
 * win over everything because the agent is blocked on the user.
 */
export function liveActivity(a, { now, asks = [], cwd, offline = false, recent = 8, idleText, }) {
    const quietMs = a.lastEventAt ? Math.max(0, now - a.lastEventAt) : 0;
    const ask = asks.at(-1);
    let current;
    const running = a.steps.findLast((s) => s.running > 0 && !s.background);
    if (offline)
        current = { kind: 'offline', text: 'Disconnected — showing the last activity seen' };
    else if (ask)
        current = { kind: 'waiting', text: `Waiting for you: ${ask.text}`, askId: ask.id };
    else if (running)
        current = {
            kind: 'tool',
            text: stepLabel(running, cwd),
            since: running.lastStartedAt,
            step: running,
        };
    else if (a.busy && a.writing)
        current = {
            kind: a.writing.type === 'thinking' ? 'thinking' : 'writing',
            text: a.writing.type === 'thinking' ? 'Thinking…' : 'Writing a reply…',
            since: a.writing.since,
        };
    else if (a.busy)
        current = {
            kind: 'working',
            text: 'Thinking…',
            since: a.steps.at(-1)?.endedAt ?? a.lastEventAt,
        };
    else
        current = {
            kind: 'idle',
            text: idleText ??
                (a.lastOutcome === 'error'
                    ? 'Stopped after an error'
                    : a.lastOutcome === 'interrupted'
                        ? 'Interrupted'
                        : a.turns
                            ? 'Idle — finished its turn'
                            : 'Not started yet'),
        };
    const out = [];
    // The step shown as "now" is not repeated in the feed: every step appears exactly once.
    for (let i = a.steps.length - 1; i >= 0 && out.length < recent; i--)
        if (a.steps[i] !== current.step)
            out.push(a.steps[i]);
    return {
        now: current,
        quietMs,
        quiet: !offline && !ask && a.busy && a.lastEventAt > 0 && quietMs >= ACTIVITY_QUIET_MS,
        recent: out,
    };
}
//# sourceMappingURL=activity.js.map